<!--
    The DOOH Campaign Results dashboard, in the order the client's design puts its sections.

    Every panel draws itself whether or not the provider answered it: an empty one shows a zeroed
    axis or a zeroed table and says, underneath, which kind of zero it is. That is the client's own
    ruling — a zero over a panel that disappears — and the note is what keeps a zero the provider
    never reported from reading as an audience of nobody.
-->
<div class="results">

    <!-- Header -->
    <div class="results__header">
        <div>
            <h1 class="results__title montserrat-semi-bold mb-1">DOOH CAMPAIGN RESULTS</h1>
            <p class="results__subtitle mb-0">{{ subtitle }}</p>
        </div>

        <div class="results__header-actions">
            <mat-form-field class="form-group dark-input results__dates">
                <mat-date-range-input [formGroup]="dateForm" [rangePicker]="resultsPicker"
                    [min]="minDate" [max]="maxDate">
                    <input matStartDate formControlName="start" readonly />
                    <input matEndDate formControlName="end" readonly />
                </mat-date-range-input>
                <mat-datepicker-toggle matSuffix [for]="resultsPicker"></mat-datepicker-toggle>
                <mat-date-range-picker #resultsPicker></mat-date-range-picker>
            </mat-form-field>

            <!-- Disabled while exporting: the run drives the page's own controls through every
                 combination, so a second run would fight the first for them. -->
            <button type="button" class="results__export" [disabled]="isExporting"
                (click)="exportPdf()">
                {{ isExporting ? 'Preparing PDF…' : 'Export PDF' }}
            </button>
        </div>
    </div>

    <!--
        The export failed. The run restores every control it moved, so the page comes back looking
        untouched and the failure would otherwise be indistinguishable from a button that did
        nothing. Dismissible, and cleared by the next attempt.
    -->
    <div class="results__disclaimer results__disclaimer--error" *ngIf="exportFailed" role="alert">
        <span class="results__disclaimer-icon">&#9888;</span>
        <p class="mb-0">We couldn't export the report. Please try again.</p>
        <button type="button" class="results__disclaimer-close" aria-label="Dismiss"
            (click)="exportFailed = false">&times;</button>
    </div>

    <!--
        The forecast caveat. Shown until the campaign is completed, because until then every figure
        on the page is a projection of a buy rather than an account of one.
    -->
    <div class="results__disclaimer" *ngIf="showForecastDisclaimer">
        <span class="results__disclaimer-icon">&#9888;</span>
        <p class="mb-0">
            <strong>Forecast data</strong> — These metrics are estimates based on the current
            campaign setup. Actual performance data will be available once the campaign is completed.
        </p>
        <button type="button" class="results__disclaimer-close" (click)="disclaimerDismissed = true"
            aria-label="Dismiss forecast notice">&times;</button>
    </div>

    <div class="results__loading" *ngIf="isLoading">
        <mat-spinner diameter="48"></mat-spinner>
    </div>

    <!--
        A failed call still leaves the page standing, zeroed. The note says why the zeroes are there:
        the client asked for zeroes over a vanishing panel, which only works if the page says which
        kind of zero it is showing.
    -->
    <p class="results__notice" *ngIf="!isLoading && hasFailed">
        Performance data could not be loaded right now. The figures below are shown as zero until it
        is available again.
    </p>
    <p class="results__notice" *ngIf="!isLoading && !hasFailed && !insights.available">
        No performance data has been reported for this campaign yet. The figures below are shown as
        zero rather than hidden.
    </p>

    <!-- 1. Campaign details -->
    <section class="results__card results__details" data-export="Campaign details">
        <div class="results__detail">
            <span class="results__detail-label">Brand Name</span>
            <span class="results__detail-value">{{ campaignDetails?.brandName || '—' }}</span>
        </div>
        <div class="results__detail">
            <span class="results__detail-label">Issuer Name</span>
            <span class="results__detail-value">{{ campaignDetails?.issuerName || '—' }}</span>
        </div>
        <div class="results__detail">
            <span class="results__detail-label">Pricing Model</span>
            <span class="results__detail-value">{{ pricingModel }}</span>
        </div>
        <div class="results__detail">
            <span class="results__detail-label">Channels</span>
            <span class="results__detail-value">{{ channelCount | number }}</span>
        </div>
        <div class="results__detail">
            <span class="results__detail-label">Screens</span>
            <span class="results__detail-value">{{ screenCount | number }}</span>
        </div>
        <div class="results__detail">
            <span class="results__detail-label">Industry</span>
            <span class="results__detail-value">{{ campaignDetails?.industryName || '—' }}</span>
        </div>
        <div class="results__detail">
            <span class="results__detail-label">Campaign Status</span>
            <span class="results__detail-value d-flex align-items-center">
                <span class="results__status-dot" [ngClass]="statusClass"></span>
                {{ statusLabel || '—' }}
            </span>
        </div>
    </section>

    <!-- 2. The four headline figures -->
    <section class="results__kpis" data-export="Headline figures">
        <div class="results__card results__kpi">
            <span class="results__kpi-label">
                UNIVERSE
                <span class="results__info" [matTooltip]="tooltips.universe"
                    matTooltipPosition="above">&#9432;</span>
            </span>
            <span class="results__kpi-value">{{ universe | number }}</span>
        </div>
        <div class="results__card results__kpi">
            <span class="results__kpi-label">
                OTS
                <span class="results__info" [matTooltip]="tooltips.ots"
                    matTooltipPosition="above">&#9432;</span>
            </span>
            <span class="results__kpi-value">{{ ots | number }}</span>
        </div>
        <div class="results__card results__kpi">
            <span class="results__kpi-label">
                LTS
                <span class="results__info" [matTooltip]="tooltips.lts"
                    matTooltipPosition="above">&#9432;</span>
            </span>
            <span class="results__kpi-value">{{ lts | number }}</span>
        </div>
        <div class="results__card results__kpi">
            <span class="results__kpi-label">
                GRP
                <span class="results__info" [matTooltip]="tooltips.grp"
                    matTooltipPosition="above">&#9432;</span>
            </span>
            <span class="results__kpi-value">{{ grp | number: '1.1-1' }}%</span>
        </div>
    </section>

    <!-- 3. The money. TMC's own figures, not the provider's — unaffected by the channel filter. -->
    <section class="results__money" data-export="Spend">
        <div class="results__card results__money-tile">
            <span class="results__kpi-label">Total Budget</span>
            <span class="results__kpi-value">{{ totalBudget | currency: currencyCode }}</span>
        </div>
        <div class="results__card results__money-tile">
            <span class="results__kpi-label">Total Actual Spend</span>
            <span class="results__kpi-value">{{ spend | currency: currencyCode }}</span>
        </div>
        <div class="results__card results__money-tile">
            <span class="results__kpi-label">Remaining Budget</span>
            <span class="results__kpi-value">{{ remainingBudget | currency: currencyCode }}</span>
        </div>
    </section>

    <!-- 4. Audience insights — one channel at a time, by the chosen metric -->
    <section class="results__card results__section" data-export="Audience insights">
        <div class="results__section-head">
            <h2 class="results__section-title montserrat-semi-bold mb-0">
                AUDIENCE INSIGHTS
                <span class="results__info" [matTooltip]="tooltips.audienceInsights"
                    matTooltipPosition="above">&#9432;</span>
            </h2>
            <div class="results__controls">
                <!-- No channel dropdown. The delivery endpoint reports no demographics, so every
                     channel comes back with the same zeroed slices and the choice was between
                     identical tables. The panel describes the campaign. -->
                <mat-form-field class="form-group dark-input results__control">
                    <mat-label>Metric</mat-label>
                    <mat-select [value]="audienceMetric" panelClass="select-panel-down"
                        [disableOptionCentering]="true" [matTooltip]="tooltips.metric"
                        matTooltipPosition="above"
                        (selectionChange)="onAudienceMetricChange($event.value)">
                        <mat-option *ngFor="let metric of metrics" [value]="metric">
                            {{ metric }}
                        </mat-option>
                    </mat-select>
                </mat-form-field>
            </div>
        </div>

        <!--
            One table, three times, stacked rather than three across: side by side each one had a
            quarter of the page and its figures were unreadable. The bands are whatever the provider
            named for this buy — four age bands and three socio-economic brackets, not the mock's
            five and four — because a padded row would be a line of figures about nobody.
        -->
        <div class="results__stack">
            <ng-container *ngTemplateOutlet="audienceTable; context: {
                $implicit: ageTable, title: 'AGE DISTRIBUTION · ' + audienceMetric }"></ng-container>
            <ng-container *ngTemplateOutlet="audienceTable; context: {
                $implicit: genderTable,
                title: 'GENDER DISTRIBUTION · ' + audienceMetric }"></ng-container>
            <ng-container *ngTemplateOutlet="audienceTable; context: {
                $implicit: incomeTable,
                title: 'SOCIO-ECONOMIC DISTRIBUTION (SEC) · ' + audienceMetric }"></ng-container>
        </div>

        <p class="results__caption mb-0" *ngIf="audienceSummable">
            Audience insights show the campaign's audience in the selected metric, across every
            channel in the buy.
        </p>
        <!--
            Reach cannot be added up across channels: the provider de-duplicates its audience, so
            one person who passed two of the campaign's screens is one reach and not two. The rows
            stay — the panel never disappears between one metric and the next — but they carry a
            dash rather than a sum nobody measured.
        -->
        <p class="results__caption mb-0" *ngIf="!audienceSummable">
            Reach is not shown per audience band for the campaign as a whole: the provider
            de-duplicates its audience across channels, so the bands cannot be added up. Switch to
            OTS or LTS for a campaign figure.
        </p>
    </section>

    <!-- 5. The channel filter every section below reads through -->
    <section class="results__card results__section">
        <div class="results__channel-filter">
            <mat-form-field class="form-group dark-input multiline-select results__control">
                <mat-label>Select Channel(s)</mat-label>
                <mat-select multiple [value]="selectedChannelIds" panelClass="select-panel-down"
                    [disableOptionCentering]="true" [matTooltip]="tooltips.channel"
                    matTooltipPosition="above"
                    (selectionChange)="onChannelFilterChange($event.value)">
                    <mat-select-trigger>
                        {{ selectedChannelIds.length }}
                        {{ selectedChannelIds.length === 1 ? 'channel' : 'channels' }} selected
                    </mat-select-trigger>
                    <mat-option *ngFor="let channel of channels" [value]="channel.channelId">
                        {{ channel.label }}
                    </mat-option>
                </mat-select>
            </mat-form-field>
            <button type="button" class="results__link" (click)="toggleAllChannels()">
                {{ allChannelsSelected ? 'Clear all' : 'Select all' }}
            </button>
        </div>
        <p class="results__caption mb-0">
            Choose one or more channels to update dashboard data. Selecting multiple channels updates
            all sections except Audience Insights, which covers the whole campaign.
        </p>
    </section>

    <!-- 6. Performance over time -->
    <section class="results__card results__section" data-export="Performance over time">
        <div class="results__section-head">
            <h2 class="results__section-title montserrat-semi-bold mb-0">
                PERFORMANCE OVER TIME
                <span class="results__info" [matTooltip]="tooltips.performanceOverTime"
                    matTooltipPosition="above">&#9432;</span>
            </h2>
            <div class="results__controls">
                <mat-form-field class="form-group dark-input results__control">
                    <mat-label>Select Metric</mat-label>
                    <mat-select [value]="trendMetric" panelClass="select-panel-down"
                        [disableOptionCentering]="true" [matTooltip]="tooltips.metric"
                        matTooltipPosition="above"
                        (selectionChange)="onTrendMetricChange($event.value)">
                        <mat-option *ngFor="let metric of metrics" [value]="metric">
                            {{ metric }}
                        </mat-option>
                    </mat-select>
                </mat-form-field>

                <!-- Defaulted from the campaign's length, then left alone once the user chooses. -->
                <div class="results__toggle">
                    <button type="button" *ngFor="let bucket of granularities"
                        [class.is-active]="granularity === bucket"
                        [attr.aria-pressed]="granularity === bucket"
                        (click)="onGranularityChange(bucket)">By {{ bucket }}</button>
                </div>
            </div>
        </div>

        <div class="results__chart">
            <canvas baseChart chartType="line" [datasets]="trendChartData"
                [labels]="trendChartLabels" [options]="trendChartOptions" [legend]="true"></canvas>
        </div>

        <!--
            The same series as a table. Three of the eight line colours sit under the 3:1 a reader
            needs to tell a thin mark from the surface, so the figures are reachable without
            resolving a colour at all — and the table is the only way to read an exact value off
            this panel, since labelling every point would bury the lines.
        -->
        <button type="button" class="results__link" [attr.aria-expanded]="trendTableVisible"
            (click)="trendTableVisible = !trendTableVisible">
            {{ trendTableVisible ? 'Hide table' : 'Show as table' }}
        </button>
        <div class="results__table-scroll" *ngIf="trendTableVisible">
            <table class="results__table">
                <thead>
                    <tr>
                        <th scope="col">Channel</th>
                        <th scope="col" class="results__num"
                            *ngFor="let bucket of trendChartLabels">{{ bucket }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let series of trendChartData">
                        <th scope="row">{{ series.label }}</th>
                        <td class="results__num" *ngFor="let value of series.data">
                            {{ value | number }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <p class="results__empty mb-0" *ngIf="!trendChartLabels.length">
            {{ isActual ? zeroNote : 'A day-by-day series is only reported once the campaign has
            run — shown as zero until then.' }}
        </p>
        <p class="results__empty mb-0" *ngIf="trendMetric === 'Reach'">
            Reach counts people, so it does not divide into days: one person seen on two days is
            still one person. This panel reads zero for Reach by the nature of the measure.
        </p>
    </section>

    <!-- 7. Performance by channel -->
    <section class="results__card results__section" data-export="Performance by channel">
        <h2 class="results__section-title montserrat-semi-bold">
            PERFORMANCE BY CHANNEL
            <span class="results__info" [matTooltip]="tooltips.performanceByChannel"
                matTooltipPosition="above">&#9432;</span>
        </h2>
        <!--
            Three single-series charts rather than one grouped one: OTS, LTS and GRP are an audience
            count, a smaller audience count and a percentage, and putting them on one axis would
            flatten two of the three to nothing.
        -->
        <div class="results__triple">
            <div class="results__subpanel">
                <h3 class="results__subpanel-title">TOTAL OTS BY CHANNEL</h3>
                <div class="results__chart results__chart--small">
                    <canvas baseChart chartType="bar" [datasets]="otsByChannelData"
                        [labels]="channelChartLabels" [options]="otsBarOptions"
                        [legend]="false"></canvas>
                </div>
            </div>
            <div class="results__subpanel">
                <h3 class="results__subpanel-title">TOTAL LTS BY CHANNEL</h3>
                <div class="results__chart results__chart--small">
                    <canvas baseChart chartType="bar" [datasets]="ltsByChannelData"
                        [labels]="channelChartLabels" [options]="ltsBarOptions"
                        [legend]="false"></canvas>
                </div>
            </div>
            <div class="results__subpanel">
                <h3 class="results__subpanel-title">GRP BY CHANNEL</h3>
                <div class="results__chart results__chart--small">
                    <canvas baseChart chartType="bar" [datasets]="grpByChannelData"
                        [labels]="channelChartLabels" [options]="grpBarOptions"
                        [legend]="false"></canvas>
                </div>
            </div>
        </div>
        <p class="results__empty mb-0" *ngIf="!channelChartLabels.length">
            No channels are selected. Choose at least one above.
        </p>
    </section>

    <!-- 8. Reach N+ by channel -->
    <section class="results__card results__section" data-export="Reach N+ by channel">
        <h2 class="results__section-title montserrat-semi-bold">
            REACH N+ DISTRIBUTION BY CHANNEL
            <span class="results__info" [matTooltip]="tooltips.reachByChannel"
                matTooltipPosition="above">&#9432;</span>
        </h2>
        <!--
            One group per channel, one bar per rung, drawn as headcounts so the channels compare at
            their real scale. The rung is a step of one hue rather than a hue of its own: 1+, 2+,
            3+ is an ordered scale, and ordering it by lightness says so without asking the reader
            to learn five colours.
        -->
        <div class="results__chart">
            <canvas baseChart chartType="bar" [datasets]="reachByChannelData"
                [labels]="reachByChannelLabels" [options]="reachByChannelOptions"
                [legend]="true"></canvas>
        </div>
        <p class="results__caption mb-0">
            Each bar is the number of people reached at least that many times; the tooltip adds the
            share of that channel's audience.
        </p>
        <p class="results__empty mb-0" *ngIf="!reachByChannelData.length">{{ zeroNote }}</p>
    </section>

    <!-- 9. GRP by city -->
    <section class="results__card results__section" data-export="GRP by city">
        <div class="results__section-head">
            <h2 class="results__section-title montserrat-semi-bold mb-0">
                GRP BY CITY
                <span class="results__info" [matTooltip]="tooltips.grpByCity"
                    matTooltipPosition="above">&#9432;</span>
            </h2>
            <div class="results__controls">
                <span class="results__control-label">View by</span>
                <div class="results__toggle" [matTooltip]="tooltips.cityChannelToggle"
                    matTooltipPosition="above">
                    <button type="button" [class.is-active]="grpView === 'City'"
                        [attr.aria-pressed]="grpView === 'City'"
                        (click)="onGrpViewChange('City')">City</button>
                    <button type="button" [class.is-active]="grpView === 'Channel'"
                        [attr.aria-pressed]="grpView === 'Channel'"
                        (click)="onGrpViewChange('Channel')">Channel</button>
                </div>
            </div>
        </div>
        <div class="results__chart">
            <canvas baseChart chartType="bar" [datasets]="grpByCityData"
                [labels]="grpByCityLabels" [options]="grpByCityOptions" [legend]="false"></canvas>
        </div>
        <!--
            The payload attributes a city to the campaign, not to a channel, so the multi-select
            cannot narrow this view. Saying so is better than a panel that quietly ignores a filter
            every other panel on the page obeys.
        -->
        <p class="results__caption mb-0" *ngIf="grpView === 'City' && isChannelSubset">
            City figures cover every channel in the campaign — the channel filter above does not
            narrow them.
        </p>
        <p class="results__empty mb-0" *ngIf="!grpByCityLabels.length">{{ zeroNote }}</p>
    </section>

    <!-- 11. Important notes, verbatim from the design -->
    <section class="results__card results__notes" data-export="Important notes">
        <h3 class="results__notes-title montserrat-semi-bold">Important notes</h3>
        <ul class="mb-0">
            <li>
                Audience insights (age, gender, socio-economic) are shown per selected channel and
                metric.
            </li>
            <li>OTS and LTS are summable across channels, days, months and campaigns.</li>
            <li>Reach, Reach N+ and GRP are not directly summable.</li>
            <li>Metrics are based on estimated campaign performance.</li>
        </ul>
    </section>

    <!-- 12. The summary bar -->
    <div class="results__summary">
        {{ summaryNarrative }}
    </div>
</div>

<!--
    The Audience Insights table, used once per breakdown. The share is of the column's own total
    rather than the row's reported share, so the figures and the percentages agree whichever metric
    the dropdown is on.
-->
<ng-template #audienceTable let-table let-title="title">
    <div class="results__subpanel">
        <h3 class="results__subpanel-title">{{ title }}</h3>
        <table class="results__table">
            <thead>
                <tr>
                    <th scope="col"><span class="sr-only">Segment</span></th>
                    <th scope="col"><span class="sr-only">Share</span></th>
                    <th scope="col" class="results__num">Audience</th>
                    <th scope="col" class="results__num">% of Total</th>
                </tr>
            </thead>
            <tbody>
                <tr *ngFor="let row of table.rows">
                    <th scope="row">{{ row.label }}</th>
                    <td class="results__bar-cell">
                        <span class="results__bar">
                            <span class="results__bar-fill"
                                [style.width.%]="audienceSummable ? row.percent : 0"></span>
                        </span>
                    </td>
                    <td class="results__num">
                        {{ audienceSummable ? (row.value | number) : '—' }}
                    </td>
                    <td class="results__num">
                        {{ audienceSummable ? (row.percent | number: '1.1-1') + '%' : '—' }}
                    </td>
                </tr>
            </tbody>
            <tfoot>
                <tr>
                    <th scope="row">Total Audience</th>
                    <td></td>
                    <td class="results__num">
                        {{ audienceSummable ? (table.total | number) : '—' }}
                    </td>
                    <!-- 0.0% when there is nothing to take a share of: a 100% of nothing would be
                         the one figure on this page that is simply untrue. -->
                    <td class="results__num">
                        {{ !audienceSummable ? '—' : (table.total > 0 ? '100.0%' : '0.0%') }}
                    </td>
                </tr>
            </tfoot>
        </table>
        <p class="results__empty mb-0" *ngIf="!table.rows.length">{{ zeroNote }}</p>
    </div>
</ng-template>
